import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronRight,
  Clock3,
  RefreshCw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { iotApi, type IoTAlert } from '../../lib/iotApi';
import { useFarmOperationalData } from '../../hooks/useFarmOperationalData';
import {
  canonicalDisplayName,
  indexCustomNames,
  resolveSectorDisplayName,
  sectorNameKey,
  type SectorDisplayNameRow,
} from '../adapters/sectorNaming';

const ALERT_POLL_MS = 30_000;

type SeverityFilter = 'all' | 'critical' | 'warning' | 'info';
type StatusFilter = 'all' | 'open' | 'acknowledged';

const severityRank = (severity: string): number => {
  const normalized = severity.toLowerCase();
  if (normalized === 'critical' || normalized === 'crit') return 3;
  if (normalized === 'warning' || normalized === 'warn') return 2;
  return 1;
};

const severityLabel = (severity: string): string => {
  const normalized = severity.toLowerCase();
  if (normalized === 'critical' || normalized === 'crit') return 'Critique';
  if (normalized === 'warning' || normalized === 'warn') return 'Avertissement';
  return 'Information';
};

const relativeAge = (iso: string): string => {
  const timestamp = Date.parse(iso);
  if (!Number.isFinite(timestamp)) return 'Date inconnue';
  const deltaMinutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (deltaMinutes < 1) return "À l'instant";
  if (deltaMinutes < 60) return `Il y a ${deltaMinutes} min`;
  const hours = Math.floor(deltaMinutes / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  return `Il y a ${Math.floor(hours / 24)} j`;
};

const alertDisplayMessage = (
  alert: IoTAlert,
  sectorDisplayName: string | null,
): string => {
  if (alert.kind.toLowerCase() === 'low_battery') {
    const voltage = alert.message.match(/(\d+(?:[.,]\d+)?)\s*V/i)?.[1]?.replace('.', ',');
    const target = sectorDisplayName || alert.node_name;
    if (target) return `Batterie faible${voltage ? ` (${voltage} V)` : ''} — ${target}`;
    return `Batterie faible${voltage ? ` (${voltage} V)` : ''}`;
  }

  if (sectorDisplayName && alert.node_name && alert.message.includes(alert.node_name)) {
    return alert.message.replace(alert.node_name, sectorDisplayName);
  }

  return alert.message;
};

const SoverdiAlertsR7: React.FC = () => {
  const { t } = useTranslation(['nav']);
  const navigate = useNavigate();
  const { topology, nodes, placement, loading: topologyLoading } = useFarmOperationalData();
  const [alerts, setAlerts] = useState<IoTAlert[]>([]);
  const alertRequestGeneration = useRef(0);
  const [customNames, setCustomNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [hasLoadedAlerts, setHasLoadedAlerts] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [refreshEpoch, setRefreshEpoch] = useState(0);
  const [ackingAlertId, setAckingAlertId] = useState<string | null>(null);
  const [ackErrorId, setAckErrorId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    let timer: number | undefined;
    const load = async (): Promise<void> => {
      const requestGeneration = ++alertRequestGeneration.current;
      setLoading(true);
      try {
        const rows = await iotApi.getJson<IoTAlert[]>('/iot/alerts');
        if (!mounted || requestGeneration !== alertRequestGeneration.current) return;
        setAlerts(Array.isArray(rows) ? rows : []);
        setHasLoadedAlerts(true);
        setUnavailable(false);
        setLoading(false);
      } catch {
        if (!mounted || requestGeneration !== alertRequestGeneration.current) return;
        setAlerts([]);
        setHasLoadedAlerts(true);
        setUnavailable(true);
        setLoading(false);
      } finally {
        // Automatic polling is serialized; explicit refresh still supersedes an older request.
        if (mounted && requestGeneration === alertRequestGeneration.current) {
          timer = window.setTimeout(() => { void load(); }, ALERT_POLL_MS);
        }
      }
    };
    void load();
    return () => {
      mounted = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [refreshEpoch]);

  useEffect(() => {
    const farmId = topology?.farmId;
    if (!farmId) return undefined;
    let mounted = true;
    iotApi
      .getJson<SectorDisplayNameRow[]>(`/iot/sector-display-names?farm_id=${encodeURIComponent(farmId)}`)
      .then((rows) => {
        if (!mounted) return;
        setCustomNames(indexCustomNames(Array.isArray(rows) ? rows : []));
      })
      .catch(() => {
        if (mounted) setCustomNames(new Map());
      });
    return () => {
      mounted = false;
    };
  }, [topology?.farmId]);

  const nodeByName = useMemo(() => {
    const index = new Map<string, (typeof nodes)[number]>();
    nodes.forEach((node) => {
      if (node.name) index.set(node.name.trim().toLowerCase(), node);
    });
    return index;
  }, [nodes]);

  const sectorDisplayNames = useMemo(() => {
    const index = new Map<string, string>();
    topology?.sites.forEach((site) => {
      site.sectors.forEach((sector) => {
        const key = sectorNameKey(site.name, sector.code);
        index.set(key, resolveSectorDisplayName({
          customDisplayName: customNames.get(key) ?? null,
          canonicalDisplayName: canonicalDisplayName(sector.parcels.map((parcel) => parcel.name)),
          siteName: site.name,
          technicalSectorCode: sector.code,
        }));
      });
    });
    return index;
  }, [topology, customNames]);

  const enriched = useMemo(() =>
    alerts.map((alert) => {
      const node = alert.node_name ? nodeByName.get(alert.node_name.trim().toLowerCase()) : undefined;
      const nodePlacement = node?.sector_id ? placement.get(node.sector_id) : undefined;
      const sectorId = nodePlacement ? `${nodePlacement.siteName}:${nodePlacement.sectorCode}` : null;
      const sectorDisplayName = sectorId ? sectorDisplayNames.get(sectorId) ?? null : null;
      return {
        alert,
        node,
        siteName: nodePlacement?.siteName ?? null,
        sectorId,
        sectorName: sectorDisplayName,
        displayMessage: alertDisplayMessage(alert, sectorDisplayName),
      };
    }), [alerts, nodeByName, placement, sectorDisplayNames]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return enriched
      .filter((row) => selectedSite === 'all' || row.siteName === selectedSite)
      .filter((row) => severity === 'all' || {
        critical: ['critical', 'crit'], warning: ['warning', 'warn'], info: ['info'],
      }[severity].includes(row.alert.severity.toLowerCase()))
      .filter((row) => status === 'all' || (status === 'open' ? row.alert.status === 'open' : row.alert.status !== 'open'))
      .filter((row) => !needle || [
        row.displayMessage,
        row.alert.message,
        row.alert.kind,
        row.alert.node_name,
        row.siteName,
        row.sectorName,
      ].some((value) => value?.toLowerCase().includes(needle)))
      .sort((a, b) => {
        const severityDelta = severityRank(b.alert.severity) - severityRank(a.alert.severity);
        if (severityDelta !== 0) return severityDelta;
        return Date.parse(b.alert.created_at) - Date.parse(a.alert.created_at);
      });
  }, [enriched, search, selectedSite, severity, status]);

  const openCount = enriched.filter((row) => row.alert.status === 'open').length;
  const criticalCount = enriched.filter((row) => row.alert.status === 'open' && severityRank(row.alert.severity) === 3).length;
  const acknowledgedCount = enriched.filter((row) => row.alert.status !== 'open').length;
  const sites = topology?.sites.map((site) => site.name) ?? [];

  const goToContext = (row: (typeof enriched)[number]) => {
    if (row.sectorId) {
      navigate(`/sectors/${encodeURIComponent(row.sectorId)}`);
      return;
    }
    if (row.node?.id) navigate(`/r6-soverdi-equipment/${encodeURIComponent(row.node.id)}`);
  };

  const acknowledgeAlert = async (alertId: string) => {
    if (ackingAlertId) return;
    setAckingAlertId(alertId);
    setAckErrorId(null);
    try {
      await iotApi.post(`/iot/alerts/${encodeURIComponent(alertId)}/ack`);
      // Do not optimistically mutate the alert: refresh from the authoritative API.
      setRefreshEpoch((value) => value + 1);
    } catch {
      setAckErrorId(alertId);
    } finally {
      setAckingAlertId(null);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 pb-24 lg:pb-6" data-testid="agrisafe-alerts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            {topology?.clientName || 'Exploitation'}
          </p>
          <h1 className="text-xl sm:text-2xl font-semibold text-[var(--text-primary)]">{t('nav:alerts')}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Surveillance des alertes réelles de l'exploitation</p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshEpoch((value) => value + 1)}
          className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-lg border border-[var(--border-light)] bg-[var(--surface)] text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--gray-50)]"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Ouvertes', value: !hasLoadedAlerts || unavailable ? '—' : openCount, testId: 'alerts-kpi-open', icon: Bell, tone: 'var(--color-warning)' },
          { label: 'Critiques', value: !hasLoadedAlerts || unavailable ? '—' : criticalCount, testId: 'alerts-kpi-critical', icon: ShieldAlert, tone: 'var(--color-danger)' },
          { label: 'Acquittées', value: !hasLoadedAlerts || unavailable ? '—' : acknowledgedCount, testId: 'alerts-kpi-acknowledged', icon: CheckCircle2, tone: 'var(--color-success)' },
          { label: 'Total', value: !hasLoadedAlerts || unavailable ? '—' : enriched.length, testId: 'alerts-kpi-total', icon: AlertTriangle, tone: 'var(--color-primary)' },
        ].map(({ label, value, testId, icon: Icon, tone }) => (
          <div key={label} className="bg-[var(--surface)] border border-[var(--border-light)] rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-[var(--text-secondary)]">{label}</p>
                <p className="text-2xl font-semibold mt-1 text-[var(--text-primary)]" data-testid={testId}>{value}</p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `color-mix(in srgb, ${tone} 12%, transparent)` }}>
                <Icon className="w-5 h-5" style={{ color: tone }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[var(--surface)] border border-[var(--border-light)] rounded-xl p-3 sm:p-4 space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setSelectedSite('all')} className={`shrink-0 min-h-[40px] px-3 rounded-lg text-sm font-medium border ${selectedSite === 'all' ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'border-[var(--border-light)] text-[var(--text-secondary)]'}`}>Tous les sites</button>
          {sites.map((site) => (
            <button key={site} onClick={() => setSelectedSite(site)} className={`shrink-0 min-h-[40px] px-3 rounded-lg text-sm font-medium border ${selectedSite === site ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'border-[var(--border-light)] text-[var(--text-secondary)]'}`}>{site}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher une alerte, un secteur ou un équipement" className="w-full min-h-[44px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] pl-10 pr-3 text-sm text-[var(--text-primary)]" />
          </label>
          <select value={severity} onChange={(event) => setSeverity(event.target.value as SeverityFilter)} className="min-h-[44px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-3 text-sm text-[var(--text-primary)]">
            <option value="all">Toutes sévérités</option><option value="critical">Critiques</option><option value="warning">Avertissements</option><option value="info">Informations</option>
          </select>
          <select value={status} onChange={(event) => setStatus(event.target.value as StatusFilter)} className="min-h-[44px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-3 text-sm text-[var(--text-primary)]">
            <option value="all">Tous les états</option><option value="open">À traiter</option><option value="acknowledged">Acquittées</option>
          </select>
        </div>
      </div>

      {(loading || topologyLoading) && enriched.length === 0 ? (
        <div className="min-h-[240px] flex items-center justify-center text-[var(--text-secondary)]">Chargement des alertes…</div>
      ) : unavailable ? (
        <div className="bg-[var(--surface)] border border-[var(--border-light)] rounded-xl p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto text-[var(--color-warning)] mb-3" />
          <p className="font-medium text-[var(--text-primary)]">Alertes indisponibles</p>
          <p className="text-sm text-[var(--text-secondary)] mt-1">La lecture a échoué. Les alertes ne sont pas interprétées comme une liste vide.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[var(--surface)] border border-[var(--border-light)] rounded-xl p-8 text-center">
          <CheckCircle2 className="w-8 h-8 mx-auto text-[var(--color-success)] mb-3" />
          <p className="font-medium text-[var(--text-primary)]">Aucune alerte pour ces filtres</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => {
            const isCritical = severityRank(row.alert.severity) === 3;
            const isWarning = severityRank(row.alert.severity) === 2;
            const tone = isCritical ? 'var(--color-danger)' : isWarning ? 'var(--color-warning)' : 'var(--color-info)';
            const hasContext = Boolean(row.sectorId || row.node?.id);
            const isOpen = row.alert.status === 'open';
            const isAcking = ackingAlertId === row.alert.id;
            const ackFailed = ackErrorId === row.alert.id;
            return (
              <article key={row.alert.id} className="bg-[var(--surface)] border border-[var(--border-light)] rounded-xl overflow-hidden" data-testid={`soverdi-alert-${row.alert.id}`}>
                <div className="flex">
                  <div className="w-1 shrink-0" style={{ backgroundColor: tone }} />
                  <div className="flex-1 min-w-0 p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold" style={{ color: tone }}>{severityLabel(row.alert.severity)}</span>
                          <span className="text-xs text-[var(--text-tertiary)]">{row.alert.kind}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isOpen ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{isOpen ? 'À traiter' : 'Acquittée'}</span>
                        </div>
                        <p className="mt-2 text-sm sm:text-base font-medium text-[var(--text-primary)]">{row.displayMessage}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--text-secondary)]">
                          {row.siteName && <span>{row.siteName}</span>}
                          {row.sectorName && <span>· {row.sectorName}</span>}
                          {row.alert.node_name && <span>· Équipement : {row.alert.node_name}</span>}
                          <span className="inline-flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" />{relativeAge(row.alert.created_at)}</span>
                        </div>
                      </div>
                      {hasContext && (
                        <button type="button" onClick={() => goToContext(row)} className="shrink-0 min-w-[44px] min-h-[44px] rounded-lg hover:bg-[var(--gray-100)] flex items-center justify-center" aria-label="Voir le contexte">
                          <ChevronRight className="w-5 h-5 text-[var(--text-tertiary)]" />
                        </button>
                      )}
                    </div>

                    {isOpen && (
                      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[var(--border-light)] pt-3">
                        <button
                          type="button"
                          disabled={Boolean(ackingAlertId)}
                          onClick={() => void acknowledgeAlert(row.alert.id)}
                          data-testid={`soverdi-alert-ack-${row.alert.id}`}
                          className="min-h-[40px] rounded-lg bg-[var(--color-primary)] px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isAcking ? 'Acquittement…' : 'Acquitter'}
                        </button>
                        <span className="text-xs text-[var(--text-tertiary)]">L’état sera confirmé après relecture du serveur.</span>
                      </div>
                    )}

                    {ackFailed && (
                      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" role="alert" data-testid={`soverdi-alert-ack-error-${row.alert.id}`}>
                        L’acquittement a échoué. L’alerte reste ouverte ; réessayez après vérification de la connexion et des autorisations.
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SoverdiAlertsR7;
